import React, { useEffect, useMemo, useState } from "react";
import "./PendingVerifications.css";

const API_ROOT    = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const DOC_BASE    = "https://pumis.puchd.ac.in/";
const MAX_KB      = 150;
const ALLOWED_MODULES = [2, 15];

export default function SanctionedPosts() {
  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);
  const deptId   = parseInt(localUser?.deptid, 10);

  const [designations, setDesignations] = useState([]);
  const [list, setList]                 = useState([]);
  const [loadingDesig, setLoadingDesig] = useState(false);
  const [loadingList, setLoadingList]   = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState("");
  const [success, setSuccess]           = useState("");

  const [form, setForm] = useState({
    DesignationID: "",
    NoOfPosts: "",
    SupportingDoc: null
  });

  const fetchDesignations = async () => {
    try {
      setLoadingDesig(true);
      const res  = await fetch(`${API_ROOT}/api/meta/designations`);
      const data = await res.json();
      setDesignations(Array.isArray(data) ? data : []);
    } catch {
      setDesignations([]);
    } finally {
      setLoadingDesig(false);
    }
  };

  const fetchList = async () => {
    try {
      setLoadingList(true);
      const res  = await fetch(`${API_ROOT}/api/employees/sanctioned-posts?moduleId=${moduleId}&deptId=${deptId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load sanctioned posts");
      setList(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err.message || "Failed to load sanctioned posts");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (!ALLOWED_MODULES.includes(moduleId)) return;
    fetchDesignations();
    fetchList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const onTextChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setForm(prev => ({ ...prev, SupportingDoc: null }));
      return;
    }
    const allowed = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!allowed.includes(file.type)) {
      setError("Only PDF, JPG, JPEG, PNG files are allowed.");
      e.target.value = "";
      setForm(prev => ({ ...prev, SupportingDoc: null }));
      return;
    }
    if (file.size > MAX_KB * 1024) {
      setError(`File must be less than ${MAX_KB} KB.`);
      e.target.value = "";
      setForm(prev => ({ ...prev, SupportingDoc: null }));
      return;
    }
    setError("");
    setForm(prev => ({ ...prev, SupportingDoc: file }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.DesignationID || !form.NoOfPosts) {
      setError("Designation and No of Posts are required.");
      return;
    }
    if (parseInt(form.NoOfPosts, 10) < 0) {
      setError("No of Posts cannot be negative.");
      return;
    }

    const payload = new FormData();
    payload.append("moduleId",      String(moduleId));
    payload.append("deptId",        String(deptId));
    payload.append("DesignationID", String(form.DesignationID));
    payload.append("NoOfPosts",     String(form.NoOfPosts));
    if (form.SupportingDoc) {
      payload.append("SupportingDoc", form.SupportingDoc);
    }

    try {
      setSubmitting(true);
      const res  = await fetch(`${API_ROOT}/api/employees/sanctioned-posts`, {
        method: "POST",
        body: payload
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to submit");

      setSuccess(data.message || "Record submitted successfully.");
      setForm({ DesignationID: "", NoOfPosts: "", SupportingDoc: null });
      const fileInput = document.getElementById("SupportingDoc");
      if (fileInput) fileInput.value = "";
      fetchList();
    } catch (err) {
      setError(err.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!ALLOWED_MODULES.includes(moduleId)) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <h3 className="ctitle">Sanctioned Posts</h3>

      {error   && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* ── Form ── */}
      <form onSubmit={onSubmit} encType="multipart/form-data">
        <div className="row g-2 align-items-end mb-3">

          <div className="col-sm-3">
            <label className="form-label mb-1">Designation</label>
            <select
              className="form-select"
              name="DesignationID"
              value={form.DesignationID}
              onChange={onTextChange}
              disabled={loadingDesig}
              required
            >
              <option value="">-- Select Designation --</option>
              {designations.map(d => (
                <option key={d.DesignationID} value={d.DesignationID}>{d.Designation}</option>
              ))}
            </select>
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">No of Sanctioned Posts</label>
            <input
              className="form-control"
              name="NoOfPosts"
              type="number"
              min="0"
              placeholder="No Of Sanctioned Posts..."
              value={form.NoOfPosts}
              onChange={onTextChange}
              required
            />
          </div>

          <div className="col-sm-3">
            <label className="form-label mb-1">
              Copy of Budget ({MAX_KB} KB)
            </label>
            <input
              className="form-control"
              id="SupportingDoc"
              name="SupportingDoc"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={onFileChange}
            />
          </div>

          <div className="col-sm-2">
            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={submitting || loadingDesig}
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>

        </div>
      </form>

      <hr />

      {/* ── List ── */}
      <h5>No Of Sanctioned Posts in Department</h5>

      {loadingList ? (
        <div className="text-muted">Loading...</div>
      ) : list.length === 0 ? (
        <div className="alert alert-info">No sanctioned posts found for this department.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle pending-verif-table">
            <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
              <tr>
                <th>SNo</th>
                <th>Post / Designation</th>
                <th>No of Posts</th>
                <th>Document</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row, idx) => (
                <tr key={row.SanctionedPostID}>
                  <td>{idx + 1}</td>
                  <td>{row.Designation}</td>
                  <td>{row.NoOfPosts}</td>
                  <td>
                    {row.SupportingDoc ? (
                      <a
                        href={DOC_BASE + row.SupportingDoc}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Document
                      </a>
                    ) : (
                      <span className="text-muted">No Document</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
